/**
 * VsmTransportEdge — V114.0 Engineering Grade
 *
 * vsm_transport — polígono único fechado (arrow shape):
 *   corpo 16px, stroke ciano 2px, fill ciano 10%.
 *   Ponta: triângulo isósceles base 24px × altura 28px, fill sólido.
 *   Geometria computada a partir de sourcePoint/targetPoint.
 * vsm_fifo  — FIFO Lane técnica: duas linhas paralelas + texto integrado.
 * vsm_info  — linha fina tracejada ciano.
 */
import React from 'react';

type VsmEdgeType = 'vsm_info' | 'vsm_fifo' | 'vsm_transport' | 'vsm_pull_signal';
type TransportIcon = 'truck' | 'ship' | 'train';

const BLOCK_ICON_RADIUS = 19.2;
const BLOCK_ICON_SLASH_X = 14.6;
const BLOCK_ICON_SLASH_Y = 11.2;
const BLOCK_ICON_FILL = 'rgba(255,255,255,0.72)';
const BLOCK_ICON_STROKE = 'rgba(220,38,38,0.9)';
const BLOCK_ICON_SLASH = 'rgba(220,38,38,0.95)';

interface VsmTransportEdgeProps {
  d: string;                                   // path string — usado só para hit-area
  sourcePoint: { x: number; y: number };
  targetPoint: { x: number; y: number };
  arrow?: { x: number; y: number; angle: number };
  edgeType?: VsmEdgeType;
  transportIcon?: TransportIcon;
  label?: string;
  isSelected?: boolean;
  isDark?: boolean;
  isBlueprint?: boolean;
  isTechnical?: boolean;
  isBlocked?: boolean;
  isPullSignalBlocked?: boolean;
  isPullSignal?: boolean;
  maxCapacity?: number;
  /** Método de transmissão da caixa de informação vinculada (V139.0) */
  transmissionMethod?: 'Eletrônico' | 'Manual';
  /** V206 — fator de encurtamento do corpo da seta (0..1). Usado para reduzir o comprimento de setas
   *  de transporte de cliente/fornecedor sem mover os cards. O corpo é centrado no eixo origem↔destino. */
  lengthScale?: number;
  /** V207 — comprimento absoluto (px) do corpo da seta de transporte, centrado no eixo origem↔destino.
   *  Quando definido, sobrepõe lengthScale para padronizar o tamanho visual entre arestas distintas. */
  fixedLength?: number;
  onClick?: (e: React.MouseEvent<SVGGElement>) => void;
  onContextMenu?: (e: React.MouseEvent<SVGGElement>) => void;
}

// ── Constantes de geometria ──────────────────────────────────────────────────
const BODY_HW  = 8;    // metade da largura do corpo (total 16px)
const HEAD_HW  = 12;   // metade da base da ponta   (total 24px)
const HEAD_LEN = 28;   // altura da ponta (28px)
const ICON_SCALE = 1.77;  // V115: escala do ícone de veículo (+15%)
// Offset perpendicular: borda do corpo (BODY_HW) + gap 8px + metade visual do ícone
// native truck height ~12px × scale ~17px → half 8.5px → total ≈ 25px
const LABEL_OFFSET = BODY_HW + 8 + 9;

function baseColor(type: VsmEdgeType | undefined, selected: boolean): string {
  if (selected) return '#2563EB';
  switch (type) {
    case 'vsm_transport': return '#06b6d4';
    case 'vsm_fifo':      return '#06b6d4';
    case 'vsm_info':      return selected ? '#2563EB' : '#00e5ff';
    default:              return '#06b6d4';
  }
}

/**
 * Constrói o path SVG fechado de uma seta (polígono único).
 * 7 pontos no sentido horário: corpo (rect) + cabeça (triângulo).
 */
function buildArrowPolygon(
  sx: number, sy: number,
  tx: number, ty: number,
  bodyHW: number, headHW: number, headLen: number,
): string {
  const ddx = tx - sx;
  const ddy = ty - sy;
  const len = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
  const ux = ddx / len;   // unit vector (direção)
  const uy = ddy / len;
  const px = -uy;          // perpendicular
  const py =  ux;

  // Ponto onde o corpo encontra a base da ponta
  const abx = tx - ux * headLen;
  const aby = ty - uy * headLen;

  const pts: [number, number][] = [
    [sx  + px * bodyHW, sy  + py * bodyHW],   // P1: source lado direito
    [abx + px * bodyHW, aby + py * bodyHW],   // P2: base corpo lado direito
    [abx + px * headHW, aby + py * headHW],   // P3: ombro direito da ponta
    [tx,                ty               ],   // P4: ponta
    [abx - px * headHW, aby - py * headHW],   // P5: ombro esquerdo da ponta
    [abx - px * bodyHW, aby - py * bodyHW],   // P6: base corpo lado esquerdo
    [sx  - px * bodyHW, sy  - py * bodyHW],   // P7: source lado esquerdo
  ];

  return 'M ' + pts.map(p => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' L ') + ' Z';
}

// ── Ícones SVG de veículos — V115.0 Industrial Outline ─────────────────────
// Stroke de 1.5px fixo via vector-effect="non-scaling-stroke".
// Coordenadas nativas centradas na origem, caminhão virado para +X (direita).
function TruckIcon({ color }: { color: string }) {
  return (
    <g
      fill="none"
      stroke={color}
      strokeWidth={0.75}
      strokeLinejoin="round"
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
    >
      <path d="M-12,-5.5 H0.5 V2.8 H-12 Z" />
      <path d="M1.5,-5.5 H7.3 L11.5,-1.1 V2.8 H1.5 Z" />
      <path d="M3,-4.3 H6.8 L9.4,-1.4 H3 Z" />
      <path d="M-12,2.8 H11.5" />
      <path d="M-9.5,-3.4 H-2" opacity={0.55} />
      <path d="M-9.5,-1.1 H-2" opacity={0.55} />
      <path d="M-9,2.8 V4.6 M6.5,2.8 V4.6" />
      <circle cx="-7.2" cy="4.8" r="2.2" />
      <circle cx="7.4"  cy="4.8" r="2.2" />
      <circle cx="-7.2" cy="4.8" r="0.75" />
      <circle cx="7.4"  cy="4.8" r="0.75" />
    </g>
  );
}

function ShipIcon({ color }: { color: string }) {
  return (
    <g fill="none" stroke={color} strokeWidth={0.7} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke">
      <path d="M-10,0 L-8,4 L8,4 L10,0 Z" />
      <rect x="-4" y="-6" width="8" height="7" rx="0.5" />
      <rect x="0" y="-9" width="2.5" height="4" rx="0.5" />
      <rect x="-3" y="-5" width="2.5" height="2" rx="0.3" />
      <rect x="0.5" y="-5" width="2.5" height="2" rx="0.3" />
    </g>
  );
}

function TrainIcon({ color }: { color: string }) {
  return (
    <g fill="none" stroke={color} strokeWidth={0.7} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke">
      <rect x="-9" y="-5" width="18" height="8" rx="1.5" />
      <rect x="-7" y="-4" width="3.5" height="3" rx="0.4" />
      <rect x="-1.5" y="-4" width="3.5" height="3" rx="0.4" />
      <rect x="4" y="-4" width="3.5" height="3" rx="0.4" />
      <circle cx="-6" cy="3.5" r="2" />
      <circle cx="6"  cy="3.5" r="2" />
    </g>
  );
}

function VehicleIcon({ type, color }: { type: TransportIcon; color: string; bg?: string }) {
  if (type === 'ship')  return <ShipIcon  color={color} />;
  if (type === 'train') return <TrainIcon color={color} />;
  return <TruckIcon color={color} />;
}

// ── Componente principal ─────────────────────────────────────────────────────
export const VsmTransportEdge: React.FC<VsmTransportEdgeProps> = ({
  d,
  sourcePoint,
  targetPoint,
  arrow,
  edgeType = 'vsm_transport',
  transportIcon = 'truck',
  label,
  isSelected = false,
  isDark = false,
  isBlueprint = false,
  isTechnical = false,
  isBlocked = false,
  isPullSignalBlocked = false,
  isPullSignal = false,
  maxCapacity,
  transmissionMethod,
  lengthScale = 1,
  fixedLength,
  onClick,
  onContextMenu,
}) => {
  const infoColor = '#00e5ff';
  const infoBlocked = edgeType === 'vsm_info' && Boolean(isPullSignalBlocked);
  const blockedInfoColor = isDark ? '#64748b' : '#64748b';
  const isPullSignalEdge = edgeType === 'vsm_pull_signal';
  // V222: estilo técnico — overrides ink preto/cinza-claro em toda a geometria de transporte
  const techInk = isDark ? '#e5e7eb' : '#1a1a1a';
  // V224: vsm_pull_signal alinhado aos tokens de kanban-retirada (paridade visual com o loop SVG)
  //   normal → teal #0e7490 · blueprint → cyan #22d3ee · technical → ink
  //   stroke/dash/width seguem os mesmos valores das setas kanban renderizadas em VsmCanvas (V193)
  const pullSignalColor = isBlueprint ? '#22d3ee' : (isTechnical ? techInk : '#0e7490');
  const pullSignalSW    = isBlueprint ? 2.2 : 1.8;
  const pullSignalDash  = isBlueprint ? '7 4' : '5 5';
  const baseColorComputed = infoBlocked ? blockedInfoColor : isBlocked ? '#dc2626' : (isPullSignalEdge || edgeType === 'vsm_info') ? (isSelected ? (isPullSignalEdge ? pullSignalColor : '#2563EB') : isPullSignalEdge ? pullSignalColor : infoColor) : baseColor(edgeType, isSelected);
  const color  = isTechnical && !infoBlocked && !isBlocked && !isSelected ? techInk : baseColorComputed;
  const manualInfoColor = isBlueprint ? '#06b6d4' : '#1e293b';
  const bg     = isDark ? '#0f172a' : '#f0f9ff';
  const isT    = edgeType === 'vsm_transport';
  const isFifo = edgeType === 'vsm_fifo';
  const isInfo = edgeType === 'vsm_info' || isPullSignalEdge;
  const vehicleRotation = targetPoint.y > sourcePoint.y ? 90 : 270;

  // V206/V207 — encurta apenas vsm_transport. Quando fixedLength (px) é definido, usa-o como
  // comprimento absoluto do corpo, centrado no eixo origem↔destino. Caso contrário, aplica lengthScale.
  const axisDx = targetPoint.x - sourcePoint.x;
  const axisDy = targetPoint.y - sourcePoint.y;
  const axisLen = Math.sqrt(axisDx * axisDx + axisDy * axisDy) || 1;
  const effectiveScale = isT
    ? (typeof fixedLength === 'number' && fixedLength > 0
        ? Math.max(0.1, Math.min(1, fixedLength / axisLen))
        : Math.max(0.1, Math.min(1, lengthScale)))
    : 1;
  const effectiveSourcePoint = effectiveScale < 1
    ? {
        x: (sourcePoint.x + targetPoint.x) / 2 - axisDx * effectiveScale / 2,
        y: (sourcePoint.y + targetPoint.y) / 2 - axisDy * effectiveScale / 2,
      }
    : sourcePoint;
  const effectiveTargetPoint = effectiveScale < 1
    ? {
        x: (sourcePoint.x + targetPoint.x) / 2 + axisDx * effectiveScale / 2,
        y: (sourcePoint.y + targetPoint.y) / 2 + axisDy * effectiveScale / 2,
      }
    : targetPoint;

  // Vetor perpendicular ao eixo da seta (para posicionar label)
  const ddx = effectiveTargetPoint.x - effectiveSourcePoint.x;
  const ddy = effectiveTargetPoint.y - effectiveSourcePoint.y;
  const pathLen = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
  const ux  =  ddx / pathLen;
  const uy  =  ddy / pathLen;
  const px  = -ddy / pathLen;
  const py  =  ddx / pathLen;

  // V208 — posição do caminhão (ícone): centrado no CORPO da seta (excluindo a ponta),
  // com offset perpendicular padrão. Assim cliente desce e fornecedor sobe.
  const bodyLen = Math.max(0, pathLen - HEAD_LEN);
  const bodyMidX = effectiveSourcePoint.x + ux * bodyLen / 2;
  const bodyMidY = effectiveSourcePoint.y + uy * bodyLen / 2;
  const lx = bodyMidX + px * LABEL_OFFSET;
  const ly = bodyMidY + py * LABEL_OFFSET;

  // V208 — texto "frequência": centralizado ao longo do eixo, do lado oposto à ponta da seta
  // (i.e. logo após o ponto de origem efetivo), alinhado tanto à seta quanto ao caminhão.
  const LABEL_TEXT_AXIS_GAP = 14;
  const textX = effectiveSourcePoint.x - ux * LABEL_TEXT_AXIS_GAP;
  const textY = effectiveSourcePoint.y - uy * LABEL_TEXT_AXIS_GAP;

  // Polígono da seta (V114)
  const arrowPoly = isT
    ? buildArrowPolygon(
        effectiveSourcePoint.x, effectiveSourcePoint.y,
        effectiveTargetPoint.x, effectiveTargetPoint.y,
        BODY_HW, HEAD_HW, HEAD_LEN,
      )
    : null;

  return (
    <g onClick={onClick} onContextMenu={onContextMenu} style={{ cursor: 'pointer' }}>

      {/* Hit area invisível — usa o path original para garantir clique */}
      <path
        d={d}
        fill="none"
        stroke="transparent"
        strokeWidth={40}
        strokeLinecap="butt"
        style={{ pointerEvents: 'stroke' }}
      />

      {/* ══ vsm_transport: polígono único ══ */}
      {isT && arrowPoly && (
        <>
          {/* Glow sutil */}
          <path
            d={arrowPoly}
            fill={color}
            fillOpacity={0.03}
            stroke={color}
            strokeWidth={5}
            strokeOpacity={0.05}
            strokeLinejoin="miter"
            style={{ pointerEvents: 'none' }}
          />
          {/* Forma principal */}
          <path
            d={arrowPoly}
            fill={color}
            fillOpacity={0.05}
            stroke={color}
            strokeWidth={1.5}
            strokeLinejoin="miter"
            style={{ pointerEvents: 'none' }}
          />
        </>
      )}

      {/* ── vsm_fifo: lane técnica limpa ── */}
      {isFifo && (() => {
        const laneY = Math.round((sourcePoint.y + targetPoint.y) / 2);
        const laneGap = 7;
        const startX = sourcePoint.x;
        const endX = targetPoint.x;
        const minX = Math.min(startX, endX);
        const maxX = Math.max(startX, endX);
        const labelText = maxCapacity ? `FIFO ${maxCapacity}` : 'FIFO';
        const glowStyle = isBlocked
          ? { filter: 'drop-shadow(0 0 8px rgba(220,38,38,0.95)) drop-shadow(0 0 18px rgba(220,38,38,0.75))', animation: 'vsmBottleneckPulse 850ms ease-in-out infinite alternate' }
          : undefined;
        return (
          <g style={{ pointerEvents: 'none', ...glowStyle }}>
            <line
              x1={startX}
              y1={laneY - laneGap}
              x2={endX}
              y2={laneY - laneGap}
              stroke={color}
              strokeWidth={2.4}
              strokeLinecap="square"
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1={startX}
              y1={laneY + laneGap}
              x2={endX}
              y2={laneY + laneGap}
              stroke={color}
              strokeWidth={2.4}
              strokeLinecap="square"
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={(minX + maxX) / 2}
              y={laneY + 3.5}
              fontSize={10}
              textAnchor="middle"
              fill={color}
              fontFamily="'Inter','Roboto Condensed','Roboto',system-ui,sans-serif"
              fontWeight={700}
              letterSpacing="0.08em"
            >
              {isBlocked ? labelText : labelText}
            </text>
          </g>
        );
      })()}

      {/* ── vsm_info: estilo conforme transmissionMethod configurado na caixa — V140.0 ── */}
      {isInfo && (() => {
        const isElectronico = transmissionMethod === 'Eletrônico' && !isPullSignal;
        const isManual      = transmissionMethod === 'Manual';

        if (isElectronico) {
          // V155.0: Zig-zag Raio — amplitude pronunciada, ângulos agudos (lightning bolt)
          const sx = sourcePoint.x, sy = sourcePoint.y;
          const tx = targetPoint.x, ty = targetPoint.y;
          const ddx2 = tx - sx, ddy2 = ty - sy;
          const len2  = Math.sqrt(ddx2 * ddx2 + ddy2 * ddy2) || 1;
          const ux2 = ddx2 / len2, uy2 = ddy2 / len2;
          const perp_x = -uy2, perp_y = ux2;
          const TAIL    = 20;
          const AMP     = Math.min(20, Math.max(14, len2 * 0.13)); // 14–20px, pronunciado
          const nZags   = Math.max(3, Math.floor(len2 / 55));
          const bodyLen = len2 - TAIL;
          const pts: string[] = [`M ${sx.toFixed(1)} ${sy.toFixed(1)}`];
          for (let i = 1; i <= nZags; i++) {
            const t    = (i / (nZags + 1)) * (bodyLen / len2);
            const bx   = sx + ddx2 * t;
            const by   = sy + ddy2 * t;
            const side = i % 2 === 0 ? 1 : -1;
            pts.push(`L ${(bx + perp_x * AMP * side).toFixed(1)} ${(by + perp_y * AMP * side).toFixed(1)}`);
          }
          pts.push(`L ${(tx - ux2 * TAIL).toFixed(1)} ${(ty - uy2 * TAIL).toFixed(1)}`);
          pts.push(`L ${tx.toFixed(1)} ${ty.toFixed(1)}`);
          return (
            <path d={pts.join(' ')} fill="none"
              stroke={infoBlocked ? blockedInfoColor : '#00e5ff'} strokeWidth={2}
              strokeLinecap="square" strokeLinejoin="miter"
              style={{ pointerEvents: 'none' }} />
          );
        }

        // Pull signal/Kanban → linha tracejada teal (paridade kanban-retirada); manual → contínua; padrão → tracejada ciano.
        return (
          <path
            d={d} fill="none"
            stroke={infoBlocked ? blockedInfoColor : isPullSignal ? pullSignalColor : isManual ? manualInfoColor : '#00e5ff'}
            strokeWidth={isPullSignal ? pullSignalSW : isManual ? 1.5 : 1.5}
            strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray={isPullSignal ? pullSignalDash : isManual ? 'none' : '5 5'}
            style={{ pointerEvents: 'none' }}
          />
        );
      })()}

      {infoBlocked && isInfo && (() => {
        const mx = (sourcePoint.x + targetPoint.x) / 2;
        const my = (sourcePoint.y + targetPoint.y) / 2;
        return (
        <g transform={`translate(${mx}, ${my})`} style={{ pointerEvents: 'none' }}>
          <circle cx={0} cy={0} r={BLOCK_ICON_RADIUS} fill="none" stroke={BLOCK_ICON_STROKE} strokeWidth={1.7} vectorEffect="non-scaling-stroke" />
          <line x1={-BLOCK_ICON_SLASH_X} y1={BLOCK_ICON_SLASH_Y} x2={BLOCK_ICON_SLASH_X} y2={-BLOCK_ICON_SLASH_Y} stroke={BLOCK_ICON_SLASH} strokeWidth={2} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </g>
        );
      })()}

      {/* ── Ponta simples apenas para informação (FIFO usa lane exclusiva) ── */}
      {isInfo && arrow && (() => {
        const sz = isInfo ? 12 : 9;
        // V224: vsm_pull_signal usa cor kanban-retirada na ponta (paridade com o loop SVG)
        const tipColor = isPullSignalEdge
          ? (infoBlocked ? blockedInfoColor : pullSignalColor)
          : color;
        // Rotacionar a ponta para seguir a direção exata da seta (funciona com diagonais)
        return (
          <>
            <circle
              cx={arrow.x}
              cy={arrow.y}
              r={18}
              fill="transparent"
              style={{ pointerEvents: 'all', cursor: 'pointer' }}
              onClick={onClick}
              onContextMenu={onContextMenu}
            />
            <g
              transform={`translate(${arrow.x},${arrow.y}) rotate(${arrow.angle})`}
              style={{ pointerEvents: 'none' }}
            >
              <path d={`M0,0 L-${sz * 1.8},-${sz} L-${sz * 1.8},${sz} Z`} fill={tipColor} />
            </g>
          </>
        );
      })()}

      {/* ── Label vsm_transport: caminhão centrado no corpo da seta + frequência alinhada ao eixo ── */}
      {isT && (
        <>
          <g transform={`translate(${lx},${ly})`} style={{ pointerEvents: 'none' }}>
            {/* Ícone do veículo — fornecedor desce invertido; cliente sobe no sentido original */}
            <g transform={`rotate(${vehicleRotation}) scale(${ICON_SCALE}, -${ICON_SCALE})`}>
              <VehicleIcon type={transportIcon} color={color} />
            </g>
          </g>
          {/* Frequência — centralizada do lado oposto à ponta da seta, alinhada ao eixo */}
          {label && (
            <text
              x={textX}
              y={textY}
              fontSize={11}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={color}
              fontFamily="'Inter','Roboto Condensed','Roboto',system-ui,sans-serif"
              fontWeight={500}
              letterSpacing="0.06em"
              opacity={0.88}
              style={{ pointerEvents: 'none' }}
            >
              {label}
            </text>
          )}
        </>
      )}

    </g>
  );
};

export type { VsmEdgeType, TransportIcon };
